// Geometry constants ported from mars-ui (photo-prints/constants.js) - see plan.md §3.5
//
// `dpi`/`minDpi` moved to `FRAMED_PRINTS_GLOBAL_SPEC` in constants/framed-prints-specs.js
// (2026-08-17) - they can now be overridden per SKU, so `getFramedPrintSpec()` is the only place
// that should read them; nothing else in the app should hard-code 300 or the old 120 threshold.
export const FRAMED_PRINTS_MIN_ZOOM = 0;
export const FRAMED_PRINTS_MAX_ZOOM = 20;
export const FRAMED_PRINTS_ZOOM_FACTOR_INCREMENT = 0.1;

// Narrower than mars-ui's - red-eye stays out pending Q6 (plan.md §7.5).
//
// `flipped` is stored in the same array but is NOT an Imagizer parameter: it is a display
// transform applied with CSS in the imagebox, exactly like `rotation`. `imagizerUrl()` ignores it.
export const FRAMED_PRINT_FILTERS = {
  autoEnhance: 'auto_enhance',
  blackAndWhite: 'black_and_white',
  flipped: 'flipped'
};

/**
 * How large an image to request per call site (plan.md §3.6).
 *
 * `tile` is deliberately a FIXED size rather than one derived from the rendered box: every tile in
 * view 2 must resolve to the identical URL so the browser cache serves all of them after the first
 * request (plan.md §7.2). Varying it per tile would stampede Imagizer with a cold derivative each.
 */
export const FRAMED_PRINT_RENDER_MODES = {
  tile: 'tile',
  preview: 'preview',
  edit: 'edit',
  print: 'print'
};

export const FRAMED_PRINT_RENDER_MODE_OPTIONS = {
  [FRAMED_PRINT_RENDER_MODES.tile]: {width: 450, quality: 60},
  [FRAMED_PRINT_RENDER_MODES.preview]: {width: 1200},
  [FRAMED_PRINT_RENDER_MODES.edit]: {width: 2400},
  // Full resolution, uncropped - the imagebox does the cropping (plan.md §3.5)
  [FRAMED_PRINT_RENDER_MODES.print]: {}
};

// The cart thumbnail rendered at Add to Cart (composables/framed-prints/thumbnail.js): the whole
// framed print, longest edge this many px.
export const FRAMED_PRINT_THUMBNAIL_MAX_DIMENSION = 300;
export const FRAMED_PRINT_THUMBNAIL_QUALITY = 0.85;

// Record-level lifecycle (plan.md §4.1) - distinct from the imagebox DPI status below
export const FRAMED_PRINT_STATUSES = {
  draft: 'draft',
  ordering: 'ordering',
  ordered: 'ordered'
};

export const FRAMED_PRINT_IMAGEBOX_STATUSES = {
  lowRes: 'low_res',
  valid: 'valid'
};

export const FRAMED_PRINT_IMAGE_SOURCES = {
  foreverFile: 'forever_file',
  upload: 'upload'
};

// The Store product `subtype` the catalog is filtered to (composables/framed-prints/api.js).
export const FRAMED_PRINT_STORE_SUBTYPE = 'framed_print';

// The Store product group whose own `primary_promotion` applies to every framed print - loaded by
// the framed-prints store's `loadPromotion()`.
export const FRAMED_PRINT_STORE_GROUP = 'framed-photo-prints';

export const FRAMED_PRINT_ORIENTATIONS = {
  portrait: 'portrait',
  landscape: 'landscape'
};

// Three states, three steps, one-to-one (plan.md §1). The former fourth state (`review`) is gone:
// a framed print is added to the cart from the configure step itself, so there is no separate
// review/collection screen to land on or come back to.
export const FRAMED_PRINT_STEPS = {
  photo: 'photo',
  selectFrame: 'select-frame',
  configure: 'configure'
};

// Set on the sign-in return URL when an anonymous visitor clicks "Log in to select from Your
// Forever Account", so the gallery picker opens by itself once they are back (photo-source.vue).
export const FRAMED_PRINT_OPEN_GALLERY_PARAM = 'open_gallery';

export const FRAMED_PRINTS_PERSIST_WAIT = 500; // debounced write-behind, matching mars-ui

// The draft retention window - ONE constant for every place it is enforced, because they have to
// agree or a record and its photo expire at different times (plan.md §4.4.3). Consumed by:
//
//   - the DynamoDB `ttl` written on each record  -> server/utils/framed-prints.js
//   - the S3 lifecycle rule on `retention=draft` -> lives in AWS, so it CANNOT import this and is
//     kept in sync by hand. Changing this value means changing the bucket rule too.
export const FRAMED_PRINTS_DRAFT_RETENTION_DAYS = 180;
